<template>
  <b-container
    class="ml-5"
  >
    <b-form-row v-if="isSummaryLoaded">
      <b-col 
        class="p-1"
        align-self="end" 
      >
        <span style="font-size: 2.2em; color: #00ff61;">
          <font-awesome-icon :icon="['fas', 'dollar-sign']" />
        </span>
      </b-col>
      <b-col
        v-if="isBuyerAccount"
        v-b-tooltip.hover 
        :title="buyerBalanceTitle"
        style="flex-basis: max-content;"
        class="align-content-center"
      >
        <div class="header__account-summary-balance">
          <span v-if="hasCashBalance">{{ formattedCashBalance }}</span>
        </div>
        <div class="header__account-summary-footer">
          Balance 
          <span
            v-if="showMinimumBalanceUsage"
            class="balance-usage"
          >
            ({{ minimumBalanceUsagePercent }}%)
          </span>
        </div>
      </b-col>
      <b-col
        v-if="!isBuyerAccount"
        v-b-tooltip.hover
        title="Total amount we have paid to you"
        class="p-2"
      >
        <div class="header__account-summary-balance">
          <span v-if="hasCashBalance">{{ formattedCashBalance }}</span>
        </div>
        <div class="header__account-summary-footer">
          Received
        </div>
      </b-col>
      <b-col 
        v-if="!isBuyerAccount"
        class="p-1 ml-3"
        align-self="end" 
      >
        <span style="font-size: 2.2em; color: #00cdff;">
          <font-awesome-icon :icon="['fas', 'hand-holding-usd']" />
        </span>
      </b-col>
      <b-col
        v-if="!isBuyerAccount" 
        v-b-tooltip.hover 
        title="Your estimated future payouts"
        class="p-2"
      >
        <div class="header__account-summary-pending">
          {{ formatCurrency(sellerPendingBalance) }}
        </div>
        <div class="header__account-summary-footer">
          Pending
        </div>
      </b-col>
    </b-form-row>

    <!-- Skeleton placeholder while loading summary -->
    <b-form-row
      v-else
      class="summary-skeleton"
      aria-label="Loading account summary"
      aria-busy="true"
    >
      <b-col
        class="p-1"
        align-self="end"
      >
        <span style="font-size: 2.2em; color: #00ff61;">
          <font-awesome-icon :icon="['fas', 'dollar-sign']" />
        </span>
      </b-col>
      <b-col class="p-2">
        <div class="skeleton-currency-bar" />
        <div class="skeleton-label-bar mt-1" />
      </b-col>
      <b-col
        class="p-1 ml-3"
        align-self="end"
      >
        <span style="font-size: 2.2em; color: #00cdff;">
          <font-awesome-icon :icon="['fas', 'hand-holding-usd']" />
        </span>
      </b-col>
      <b-col class="p-2">
        <div class="skeleton-currency-bar" />
        <div class="skeleton-label-bar mt-1" />
      </b-col>
    </b-form-row>
  </b-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import formatCurrency from '@/utils/formatCurrency'

export default {
  name: 'AccountSummaryHeader',

  computed: {

    ...mapState('billing', [
      'accountSummary'
    ]),

    ...mapGetters('auth', [
      'isBuyerAccount',
      'accountDetails'
    ]),

    ...mapGetters('billing', [
      'sellerPendingBalance',
      'accountBillingConfiguration']),

    hasCashBalance() {
      // ensure we only show formatted currency when we have a finite numeric value
      const value = this.accountSummary?.cashBalance
      return Number.isFinite(value)
    },

    formattedCashBalance() {
      return this.hasCashBalance ? this.formatCurrency(this.accountSummary.cashBalance) : '...'
    },

    formattedMinimumBalance() {
      return this.formatCurrency(this.accountBillingConfiguration.minimumBalance)
    },

    isSummaryLoaded() {
      return this.hasCashBalance && this.accountSummary?.cashBalance !== null
    },

    showMinimumBalanceUsage() {
      return this.isBuyerAccount && this.accountDetails?.billingConfiguration?.prePay === false
    },

    minimumBalanceUsagePercent() {
      if (!this.showMinimumBalanceUsage) {
        return 0
      }

      const current = this.accountSummary.cashBalance
      if (current >= 0) {
        return 0
      }
      const usage = Math.abs(current / this.accountBillingConfiguration.minimumBalance) * 100
      return usage.toFixed(0)
    },

    buyerBalanceTitle() {
      if (this.showMinimumBalanceUsage) {
        return `${this.minimumBalanceUsagePercent}% of your available credit used`
      }
      return 'Your total account balance'
    }
  },

  async created () {
    await this.fetchUsersAccountDetails()

    //fetch immediately upon very first page load
    await this.getAccountBillingSummary()
    await this.loadAccountSummaryInIntervals()
  },

  destroyed () {
    clearInterval(this.accountSummaryInterval)
  },

  methods: {
    ...mapActions('billing', ['getAccountBillingSummary']),
    ...mapActions('auth', ['fetchUsersAccountDetails']),

    formatCurrency,

    async loadAccountSummaryInIntervals() {
      // Fetch and show account balance summary every 45 secs
      this.accountSummaryInterval = setInterval(async () => {
        await this.getAccountBillingSummary()
      }, 45 * 1000)
    },
  }
}
</script>

<style lang="scss" scoped>
.header__account-summary-balance {
  color: #00ff61; 
  line-height: 20px;
  font-weight: 600;
}

.header__account-summary-pending {
  color: #00cdff;
  line-height: 20px;
  font-weight: 600;
}

.header__account-summary-footer {
  color: #cfcfd0; 
  font-size: 12px;
}

.balance-usage {
  font-size: 11px;
  // color: #f2c94c;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Skeleton row styles */
.summary-skeleton {
  .skeleton-currency-bar,
  .skeleton-label-bar {
    border-radius: 4px;
    background: linear-gradient(90deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.25) 50%, rgba(255,255,255,0.08) 100%);
    background-size: 200% 100%;
    animation: shimmer 1.2s ease-in-out infinite;
  }
  .skeleton-currency-bar { height: 18px; width: 110px; }
  .skeleton-label-bar { height: 10px; width: 60px; }
}
</style>
